/**
 * Shared Combat UI Components
 * Shared combat UI components used by the Grid Combat Simulator.
 */

import { Heart, Zap, Shield, Skull, Check, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { APCombatParticipant, DicePoolPreview, ResolutionResult } from '@omen-quest/combat-engine';

// ============================================================================
// HELPERS
// ============================================================================

function pct(current: number, max: number): number {
  if (max <= 0) return 0;
  return Math.min(100, Math.max(0, (current / max) * 100));
}

function getVitalsColor(percentage: number): string {
  if (percentage > 60) return 'bg-green-500';
  if (percentage > 30) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ============================================================================
// PARTICIPANT ROW
// ============================================================================

interface ParticipantRowProps {
  participant: APCombatParticipant;
  pendingDamage?: number;
  isSelected?: boolean;
  onClick?: () => void;
  compact?: boolean;
}

export function ParticipantRow({ participant, pendingDamage, isSelected, onClick, compact }: ParticipantRowProps) {
  const vitalsPct = pct(participant.vitals, participant.vitalsMax);
  const resolvePct = pct(participant.resolve, participant.resolveMax);
  const apPct = pct(participant.apRemaining, participant.apMax);

  const hasPending = pendingDamage !== undefined && pendingDamage > 0;
  const afterDamagePct = hasPending
    ? pct(Math.max(0, participant.vitals - pendingDamage), participant.vitalsMax)
    : vitalsPct;
  const pendingPctVal = vitalsPct - afterDamagePct;

  const activeConditions = participant.conditions.filter(c => c !== 'riposte_ready');

  return (
    <div
      onClick={onClick}
      className={cn(
        'px-2 py-1.5 rounded border flex items-center gap-2 text-xs',
        participant.isDefeated
          ? 'opacity-40 bg-red-500/5 border-red-500/30'
          : 'bg-white/5 border-white/10',
        participant.type === 'player' && !participant.isDefeated && 'border-amber-500/30',
        isSelected && 'ring-1 ring-amber-400 border-amber-400/50',
        onClick && 'cursor-pointer hover:bg-white/10',
      )}
    >
      {/* Status icon */}
      {participant.isDefeated ? (
        <Skull className="w-3 h-3 text-red-400 flex-shrink-0" />
      ) : participant.hasCommitted ? (
        <Check className="w-3 h-3 text-green-400 flex-shrink-0" />
      ) : (
        <Clock className="w-3 h-3 text-yellow-400 flex-shrink-0" />
      )}

      {/* Name */}
      <span className={cn(
        'font-medium text-white truncate flex-shrink-0',
        compact ? 'w-20' : 'w-28',
        participant.isDefeated && 'line-through text-neutral-500'
      )}>
        {participant.name}
      </span>

      {/* Vitals bar */}
      <div className="flex items-center gap-1 flex-1 min-w-0">
        <Heart className="w-3 h-3 text-neutral-500 flex-shrink-0" />
        <div className="relative h-1.5 bg-black/50 rounded-full overflow-hidden flex-1">
          <div className={cn('absolute left-0 top-0 h-full', getVitalsColor(afterDamagePct))} style={{ width: `${afterDamagePct}%` }} />
          {pendingPctVal > 0 && (
            <div className="absolute top-0 h-full bg-amber-500/70 animate-pulse" style={{ left: `${afterDamagePct}%`, width: `${pendingPctVal}%` }} />
          )}
        </div>
        <span className="text-neutral-400 w-16 text-right flex-shrink-0">
          {participant.vitals}/{participant.vitalsMax}
          {hasPending && <span className="text-amber-400 ml-0.5">(~-{Math.round(pendingDamage)})</span>}
        </span>
      </div>

      {/* Resolve bar */}
      {!compact && (
        <div className="flex items-center gap-1 w-20 flex-shrink-0">
          <Zap className="w-3 h-3 text-purple-400 flex-shrink-0" />
          <div className="h-1.5 bg-black/50 rounded-full overflow-hidden flex-1">
            <div className="h-full bg-purple-500" style={{ width: `${resolvePct}%` }} />
          </div>
          <span className="text-purple-400/70 w-6 text-right">{participant.resolve}</span>
        </div>
      )}

      {/* AP bar */}
      <div className="flex items-center gap-1 w-20 flex-shrink-0">
        <Zap className="w-3 h-3 text-neutral-500 flex-shrink-0" />
        <div className="h-1.5 bg-black/50 rounded-full overflow-hidden flex-1">
          <div className="h-full bg-blue-500" style={{ width: `${apPct}%` }} />
        </div>
        <span className="text-neutral-400 w-6 text-right">{participant.apRemaining}</span>
      </div>

      {/* Defense badge */}
      {participant.defense > 0 && (
        <div className="flex items-center gap-0.5 flex-shrink-0 px-1 py-0.5 rounded bg-sky-500/15 border border-sky-500/30">
          <Shield className="w-3 h-3 text-sky-400" />
          <span className="text-sky-400 font-medium">{participant.defense}</span>
        </div>
      )}

      {/* Conditions */}
      {activeConditions.length > 0 && (
        <div className="flex gap-1 flex-shrink-0">
          {activeConditions.map((cond, i) => {
            const isNegative = cond.startsWith('burn:') || cond.startsWith('stunned:');
            return (
              <span key={i} className={cn(
                'px-1 py-0.5 rounded text-[10px]',
                isNegative ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
              )}>
                {cond}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DICE POOL INFO
// ============================================================================

export function DicePoolInfo({ pool }: { pool: DicePoolPreview }) {
  const { poolSize, probabilities } = pool;
  const p = (v: number) => Math.round(v * 100);

  const tooltip = `Fail: ${p(probabilities.fail)}% | Struggle: ${p(probabilities.struggle)}% | Success: ${p(probabilities.success)}% | Critical: ${p(probabilities.critical)}%`;

  return (
    <span className="inline-flex items-center gap-1 ml-1" title={tooltip}>
      <span className="text-amber-400 font-mono text-[10px]">{poolSize}d6</span>
      <span className="inline-flex w-10 h-1.5 rounded-full overflow-hidden bg-black/50">
        <span className="h-full bg-red-500" style={{ width: `${p(probabilities.fail)}%` }} />
        <span className="h-full bg-yellow-500" style={{ width: `${p(probabilities.struggle)}%` }} />
        <span className="h-full bg-green-500" style={{ width: `${p(probabilities.success)}%` }} />
        <span className="h-full bg-blue-500" style={{ width: `${p(probabilities.critical)}%` }} />
      </span>
    </span>
  );
}

// ============================================================================
// COMBAT LOG VIEWER
// ============================================================================

export function CombatLogViewer({ log, maxHeight = 'max-h-48' }: { log: ResolutionResult[]; maxHeight?: string }) {
  if (log.length === 0) return null;

  return (
    <div className="border border-white/10 rounded overflow-hidden">
      <div className="px-2 py-1 bg-white/5 border-b border-white/10 text-xs">
        <span className="font-medium text-white">Combat Log ({log.length})</span>
      </div>
      <div className={cn('px-2 py-1 bg-black/30 overflow-y-auto space-y-0', maxHeight)}>
        {log.map((result, i) => (
          <div key={i} className="text-xs text-neutral-400 py-0.5">
            <span className="text-white">{result.actorName}</span>
            {' '}{result.actionType}
            {result.targetName && <> → <span className="text-neutral-300">{result.targetName}</span></>}
            {result.diceResult && (
              <span className={cn(
                'ml-1 font-mono',
                result.diceResult.result === 'critical' ? 'text-amber-400' :
                result.diceResult.result === 'success' ? 'text-green-400' :
                result.diceResult.result === 'struggle' ? 'text-yellow-400' :
                'text-red-400'
              )}>
                [{result.diceResult.rolls.join(',')}]
              </span>
            )}
            {result.damage !== undefined && result.damage > 0 && (
              <span className="text-red-400 ml-1">-{result.damage}</span>
            )}
            {result.isDefeat && <span className="text-red-400 ml-1 font-bold">DEFEATED</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
